import { PLAYER_ASSERTION_MAX_LENGTH, parseEmbedOrigins } from "@cwb/protocol";

/**
 * The game's side of the portal handshake (justaigame.com).
 *
 * When the game runs in an iframe, the portal tells it who is playing with a
 * signed, short-lived assertion. The game's only job is to carry that assertion
 * to its own server, which reports the finished score under that account; the
 * browser never submits a score and never inspects the assertion.
 *
 *   game   -> portal  { type: "jp:ready" }
 *   portal -> game    { type: "jp:player", assertion: string | null, expiresAt: number | null }
 *   game   -> portal  { type: "jp:player-request" }        (asks for a fresh one)
 *
 * Everything crossing that boundary is untrusted until it passes three gates:
 * the sender's origin is on the allowed list, the sender is our actual parent
 * window, and the payload is exactly the documented shape. Anything else is
 * dropped silently — a page that can reach `postMessage` is not necessarily
 * the portal.
 *
 * The assertion is a credential for one score. It is held in memory only: never
 * in a URL, storage, the DOM, or the console.
 */

export const PORTAL_MESSAGE = {
  ready: "jp:ready",
  player: "jp:player",
  playerRequest: "jp:player-request",
} as const;

/** Ask the portal for a fresh assertion once the held one has this little time left. */
export const REFRESH_WINDOW_MS = 60_000;
/**
 * The idle check looks further ahead: a hidden tab runs timers about once a
 * minute, so a 60 s window could be missed entirely between two ticks.
 */
const POLL_REFRESH_WINDOW_MS = 180_000;
/** ...but never more often than this, so a slow portal is not hammered. */
const REQUEST_MIN_INTERVAL_MS = 5_000;
/** How often an idle game checks whether its assertion is running out. */
const POLL_MS = 30_000;
/** Below this a number is epoch seconds, not milliseconds (1e11 ms is 1973). */
const EPOCH_SECONDS_CEILING = 1e11;

export interface PlayerMessage {
  type: typeof PORTAL_MESSAGE.player;
  assertion: string | null;
  expiresAt: number | null;
}

/**
 * Exactly `{ type, assertion, expiresAt }`: no other keys, `assertion` a
 * non-empty string within the server's cap or null, `expiresAt` a finite number
 * or null. Returns null for anything else.
 */
export function parsePlayerMessage(data: unknown): PlayerMessage | null {
  if (typeof data !== "object" || data === null || Array.isArray(data)) return null;
  const keys = Object.keys(data);
  if (keys.length !== 3 || !["type", "assertion", "expiresAt"].every((key) => keys.includes(key))) {
    return null;
  }
  const { type, assertion, expiresAt } = data as Record<string, unknown>;
  if (type !== PORTAL_MESSAGE.player) return null;
  const assertionOk =
    assertion === null ||
    (typeof assertion === "string" && assertion.length > 0 && assertion.length <= PLAYER_ASSERTION_MAX_LENGTH);
  const expiresOk = expiresAt === null || (typeof expiresAt === "number" && Number.isFinite(expiresAt));
  if (!assertionOk || !expiresOk) return null;
  return { type, assertion: assertion as string | null, expiresAt: expiresAt as number | null };
}

/** Origins from `VITE_EMBED_PARENT_ORIGINS`; unset means the shared default list. */
export function allowedParentOrigins(raw: string | undefined): string[] {
  // Unlike the server, a bad entry cannot stop the page: it is dropped, and a
  // list with nothing valid left leaves the bridge inert rather than open.
  return parseEmbedOrigins(raw).origins;
}

interface PostTarget {
  postMessage(message: unknown, targetOrigin: string): void;
}

export interface BridgeMessageEvent {
  origin: string;
  source: unknown;
  data: unknown;
}

/** The slice of `window` the bridge needs, so tests can supply their own. */
export interface BridgeHost {
  readonly parent: unknown;
  addEventListener(type: "message", listener: (event: BridgeMessageEvent) => void): void;
  removeEventListener(type: "message", listener: (event: BridgeMessageEvent) => void): void;
}

export interface PortalBridgeOptions {
  host: BridgeHost;
  allowedOrigins: readonly string[];
  now?: () => number;
  /** 0 disables the idle expiry check (tests drive refreshIfExpiring directly). */
  pollMs?: number;
}

export interface PortalBridge {
  /** True only inside an iframe with at least one allowed parent origin. */
  readonly active: boolean;
  start(): void;
  stop(): void;
  /** The latest assertion the portal sent, or null. */
  current(): string | null;
  /** Called whenever the held assertion changes (including to or from null). */
  subscribe(listener: (assertion: string | null) => void): () => void;
  /** Ask the portal for a new assertion if the held one lapses within `windowMs` (default 60 s). */
  refreshIfExpiring(windowMs?: number): void;
  /**
   * Ask the portal who is playing when nothing is held. The one `jp:ready` at
   * boot can reach a portal that is not listening yet; asking again when the
   * game joins its session closes that gap. Shares the request throttle.
   */
  requestIfMissing(): void;
}

export function createPortalBridge(options: PortalBridgeOptions): PortalBridge {
  const { host } = options;
  const now = options.now ?? Date.now;
  const origins = new Set(options.allowedOrigins);
  const parent = host.parent;
  const embedded = parent !== host && typeof parent === "object" && parent !== null;
  const active = embedded && origins.size > 0;

  let latest: { assertion: string; expiresAtMs: number | null } | null = null;
  let lastRequestMs = Number.NEGATIVE_INFINITY;
  let started = false;
  let timer: ReturnType<typeof setInterval> | null = null;
  const listeners = new Set<(assertion: string | null) => void>();

  const postToParent = (message: { type: string }): void => {
    // One post per allowed origin, each with that exact target: the browser
    // delivers it only if the parent really is that origin. Never "*".
    for (const origin of origins) {
      try {
        (parent as PostTarget).postMessage(message, origin);
      } catch {
        // A mismatched target is dropped by the browser; nothing to recover.
      }
    }
  };

  const apply = (message: PlayerMessage): void => {
    const before = latest?.assertion ?? null;
    latest =
      message.assertion === null
        ? null
        : { assertion: message.assertion, expiresAtMs: toEpochMs(message.expiresAt) };
    const after = latest?.assertion ?? null;
    if (after !== before) for (const listener of [...listeners]) listener(after);
  };

  const onMessage = (event: BridgeMessageEvent): void => {
    if (!origins.has(event.origin)) return;
    if (event.source !== parent) return;
    const message = parsePlayerMessage(event.data);
    if (message) apply(message);
  };

  const requestPlayer = (): void => {
    const at = now();
    if (at - lastRequestMs < REQUEST_MIN_INTERVAL_MS) return;
    lastRequestMs = at;
    postToParent({ type: PORTAL_MESSAGE.playerRequest });
  };

  const refreshIfExpiring = (windowMs: number = REFRESH_WINDOW_MS): void => {
    if (!active || latest === null || latest.expiresAtMs === null) return;
    if (latest.expiresAtMs - now() > windowMs) return;
    requestPlayer();
  };

  const requestIfMissing = (): void => {
    if (active && latest === null) requestPlayer();
  };

  return {
    active,
    start() {
      if (!active || started) return;
      started = true;
      host.addEventListener("message", onMessage);
      postToParent({ type: PORTAL_MESSAGE.ready });
      const pollMs = options.pollMs ?? POLL_MS;
      if (pollMs > 0) {
        timer = setInterval(() => refreshIfExpiring(POLL_REFRESH_WINDOW_MS), pollMs);
      }
    },
    stop() {
      if (!started) return;
      started = false;
      host.removeEventListener("message", onMessage);
      if (timer !== null) clearInterval(timer);
      timer = null;
    },
    current: () => latest?.assertion ?? null,
    subscribe(listener) {
      listeners.add(listener);
      return () => void listeners.delete(listener);
    },
    refreshIfExpiring,
    requestIfMissing,
  };
}

function toEpochMs(expiresAt: number | null): number | null {
  if (expiresAt === null) return null;
  return expiresAt < EPOCH_SECONDS_CEILING ? expiresAt * 1000 : expiresAt;
}

let instance: PortalBridge | null = null;

/** The page's single bridge, built from `window` and the Vite environment. */
export function getPortalBridge(): PortalBridge {
  instance ??= createPortalBridge({
    host: window as unknown as BridgeHost,
    allowedOrigins: allowedParentOrigins(import.meta.env.VITE_EMBED_PARENT_ORIGINS as string | undefined),
  });
  return instance;
}

/** Call once at boot. Standalone (not in an iframe) this does nothing at all. */
export function startPortalBridge(): PortalBridge {
  const bridge = getPortalBridge();
  bridge.start();
  return bridge;
}
